import { useState } from 'react';
import { FileUpload, UploadedFile } from './FileUpload';

interface FileUploadSectionProps {
  noteFile: UploadedFile | null;
  sourceFile: UploadedFile | null;
  onNoteFileChange: (file: UploadedFile | null) => void;
  onSourceFileChange: (file: UploadedFile | null) => void;
}

export function FileUploadSection({
  noteFile,
  sourceFile,
  onNoteFileChange,
  onSourceFileChange,
}: FileUploadSectionProps) {
  const [isUploadingNote, setIsUploadingNote] = useState(false);
  const [isUploadingSource, setIsUploadingSource] = useState(false);

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center">
          <span className="text-primary-400 font-semibold text-sm">1</span>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-surface-100">Upload Your Files</h3>
          <p className="text-sm text-surface-400">Add your notes and optionally a source document</p>
        </div>
      </div>

      {/* Upload Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Notes Upload (Required) */}
        <FileUpload
          type="note"
          label="Notes / Highlights"
          description="Your notes, highlights, or annotations"
          acceptedFormats={['.txt', '.html', '.docx']}
          onFileUploaded={(file) => onNoteFileChange(file)}
          onFileRemoved={() => onNoteFileChange(null)}
          uploadedFile={noteFile}
          isUploading={isUploadingNote}
          onUploadStart={() => setIsUploadingNote(true)}
          onUploadEnd={() => setIsUploadingNote(false)}
          required
        />

        {/* Source Upload (Optional) */}
        <FileUpload
          type="source"
          label="Source Document"
          description="The book, article, or PDF your notes are from"
          acceptedFormats={['.pdf', '.epub']}
          onFileUploaded={(file) => onSourceFileChange(file)}
          onFileRemoved={() => onSourceFileChange(null)}
          uploadedFile={sourceFile}
          isUploading={isUploadingSource}
          onUploadStart={() => setIsUploadingSource(true)}
          onUploadEnd={() => setIsUploadingSource(false)}
        />
      </div>

      {/* Help Text */}
      <div className="bg-surface-800 rounded-lg p-4 border border-surface-700">
        <div className="flex gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-sm text-surface-400">
            <p className="mb-2">
              <strong className="text-surface-300">Notes file:</strong> Upload your highlights, annotations, or reading notes.
              Supports plain text (.txt), HTML exports from Kindle/Apple Books (.html), or Word documents (.docx).
            </p>
            <p>
              <strong className="text-surface-300">Source document (optional):</strong> Adding the original book or article
              helps the AI provide more context and accurate citations. Supports PDF and EPUB formats.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
